// @givmo/app-charity — running a payout mutation through the step-up refusal (AS-WEB3).
//
// The gate itself is `@givmo/step-up`'s (CCM-R1-STEP-UP): this binds it to the console's session
// and payout's own words, under the test ids payout has always kept (`payment-step-up-*`).
// Behaviour is unchanged: one shared prompt, the forced token refresh, and one retry of the very
// operation that was refused.
//
// Under the console's provider (every payout page) the two forms join its ONE prompt
// (CCM-R1-STEP-UP-B, P4). The payment page shows one of them at a time; payout's join test composes
// both. A form rendered where no provider exists keeps a prompt of its own on the console's host
// (`useCharityStepUpHost`).
//
//   mutationFn: (values) => gate.run(() => sdk.charityAdminSubmitCharityAch({ …values }))

import {
  useOptionalStepUpHost,
  useStepUpGate as useSharedStepUpGate,
  type StepUpGate,
} from '@givmo/step-up';
import { useCharityStepUpHost } from '../step-up/charity-step-up-host';
import { PAYOUT_REENTRY_STEP_UP_COPY, PAYOUT_STEP_UP_COPY } from './payout-step-up-copy';

export type { StepUpGate } from '@givmo/step-up';

/**
 * `reentry`: the bank form was opened after a change of ownership (CPA-BE69), so its prompt drops
 * "new" as the form does (`PAYOUT_REENTRY_STEP_UP_COPY`).
 */
export function useStepUpGate({ reentry = false }: { reentry?: boolean } = {}): StepUpGate {
  const host = useCharityStepUpHost();
  const provided = useOptionalStepUpHost();
  return useSharedStepUpGate({
    ...(provided === null ? { host } : {}),
    copy: reentry ? PAYOUT_REENTRY_STEP_UP_COPY : PAYOUT_STEP_UP_COPY,
    testIdPrefix: 'payment-step-up',
  });
}
